import { useState, useEffect, useRef } from "react";
import { useAuth, api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { User, Mail, Calendar, Clock, Edit2, AlertTriangle, Loader2, Check, X, Lock, Eye, EyeOff, Camera, Upload, Trash2, Bell, Palette, HelpCircle, ChevronRight, Settings } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import AppLogo from "@/components/AppLogo";
import { useTheme } from "@/contexts/ThemeContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function ProfilePage() {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { theme = "pink", setTheme, themes = [] } = useTheme() || {};
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  
  // Profile photo state
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [removingPhoto, setRemovingPhoto] = useState(false);
  
  // Password change state
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const fetchProfile = async () => {
    try {
      const res = await api.get("/auth/me");
      setProfile(res.data);
      setNewName(res.data.name);
    } catch (error) {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  // Auto-expand password section if forced
  useEffect(() => {
    if (user?.force_password_change) {
      setShowPasswordSection(true);
    }
  }, [user]);

  const handleSaveName = async () => {
    if (!newName.trim()) {
      toast.error("Name cannot be empty");
      return;
    }

    setSaving(true);
    try {
      await api.put("/auth/settings", { name: newName.trim() });
      setProfile(prev => ({ ...prev, name: newName.trim() }));
      updateUser({ name: newName.trim() });
      setEditingName(false);
      toast.success("Name updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update name");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    setDeactivating(true);
    try {
      await api.post("/auth/deactivate");
      toast.success("Account deactivated");
      logout();
      navigate("/login");
    } catch (error) {
      toast.error("Failed to deactivate account");
    } finally {
      setDeactivating(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("Please fill in all password fields");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setChangingPassword(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      // Clear the force_password_change flag if it was set
      if (user?.force_password_change) {
        updateUser({ force_password_change: false });
      }
      
      toast.success("Password updated successfully");
      setShowPasswordSection(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update password");
    } finally {
      setChangingPassword(false);
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload a JPG, JPEG, or PNG image");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image must be less than 2MB");
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await api.post("/profile/photo", formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setProfile(prev => ({ ...prev, profile_photo_url: res.data.profile_photo_url }));
      updateUser({ profile_photo_url: res.data.profile_photo_url });
      toast.success("Profile photo updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemovePhoto = async () => {
    setRemovingPhoto(true);
    try {
      await api.delete("/profile/photo");
      setProfile(prev => ({ ...prev, profile_photo_url: null }));
      updateUser({ profile_photo_url: null });
      toast.success("Profile photo removed");
    } catch (error) {
      toast.error("Failed to remove photo");
    } finally {
      setRemovingPhoto(false);
    }
  };

  // Check if password change is forced
  const isForcePasswordChange = user?.force_password_change;

  if (loading) {
    return (
      <div className="page-container">
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="profile-page">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <AppLogo size="small" showText={false} />
            <h1 className="font-heading text-lg font-bold">Profile</h1>
          </div>
        </div>
      </header>

      {/* Force Password Change Banner */}
      {isForcePasswordChange && (
        <div className="bg-orange-100 border-b border-orange-200 px-4 py-3">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-orange-600" />
            <p className="text-sm font-medium text-orange-800">
              You must change your password before continuing.
            </p>
          </div>
        </div>
      )}

      <main className="p-4 space-y-6 pb-24">
        {/* Profile Avatar & Name */}
        <Card className="animate-fadeIn">
          <CardContent className="p-6">
            <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
              {/* Profile Photo */}
              <div className="relative group">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  capture="environment"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  data-testid="profile-photo-input"
                />
                
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="relative h-24 w-24 rounded-full overflow-hidden bg-primary/10 flex items-center justify-center cursor-pointer hover:opacity-90 transition-opacity touch-manipulation"
                  data-testid="profile-photo-avatar"
                >
                  {profile?.profile_photo_url ? (
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}${profile.profile_photo_url}`}
                      alt="Profile"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <User size={40} className="text-primary" />
                  )}
                  
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                    {uploadingPhoto ? (
                      <Loader2 size={24} className="text-white animate-spin" />
                    ) : (
                      <Camera size={24} className="text-white" />
                    )}
                  </div>
                </button>
                
                {profile?.profile_photo_url && !uploadingPhoto && (
                  <Button
                    size="icon"
                    variant="destructive"
                    onClick={handleRemovePhoto}
                    disabled={removingPhoto}
                    className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full shadow-lg"
                    data-testid="profile-remove-photo-btn"
                  >
                    {removingPhoto ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Trash2 size={14} />
                    )}
                  </Button>
                )}
              </div>
              
              {/* Name and email */}
              <div className="flex-1 text-center sm:text-left">
                {editingName ? (
                  <div className="flex items-center gap-2 justify-center sm:justify-start">
                    <Input
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      className="h-10 max-w-[200px]"
                      placeholder="Your name"
                      data-testid="profile-name-input"
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={handleSaveName}
                      disabled={saving}
                      className="h-10 w-10 rounded-full"
                      data-testid="profile-save-name-btn"
                    >
                      {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => {
                        setEditingName(false);
                        setNewName(profile?.name || "");
                      }}
                      className="h-10 w-10 rounded-full"
                    >
                      <X size={16} />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 justify-center sm:justify-start">
                    <h2 className="font-heading text-xl font-bold">{profile?.name}</h2>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setEditingName(true)}
                      className="h-8 w-8 rounded-full"
                      data-testid="profile-edit-name-btn"
                    >
                      <Edit2 size={14} />
                    </Button>
                  </div>
                )}
                <p className="text-sm text-muted-foreground mt-1">{profile?.email}</p>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="mt-3 rounded-full sm:hidden"
                  data-testid="profile-upload-photo-btn"
                >
                  {uploadingPhoto ? (
                    <Loader2 size={14} className="mr-2 animate-spin" />
                  ) : (
                    <Upload size={14} className="mr-2" />
                  )}
                  {profile?.profile_photo_url ? "Change Photo" : "Upload Photo"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Details */}
        <Card className="animate-fadeIn" style={{ animationDelay: "0.05s" }}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Account Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-2 border-b border-border/50">
              <div className="flex items-center gap-3">
                <Mail size={18} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Email</span>
              </div>
              <span className="text-sm font-medium">{profile?.email}</span>
            </div>
            
            <div className="flex items-center justify-between py-2 border-b border-border/50">
              <div className="flex items-center gap-3">
                <Calendar size={18} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Member since</span>
              </div>
              <span className="text-sm font-medium">
                {profile?.created_at ? format(parseISO(profile.created_at), "MMM d, yyyy") : "-"}
              </span>
            </div>
            
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <Clock size={18} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Access expires</span>
              </div>
              <span className="text-sm font-medium">
                {profile?.access_expires_at 
                  ? format(parseISO(profile.access_expires_at), "MMM d, yyyy")
                  : "Not set"
                }
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Security */}
        <Card className="animate-fadeIn" style={{ animationDelay: "0.1s" }}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Lock size={18} />
              Security
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!showPasswordSection ? (
              <Button
                variant="outline"
                className="w-full h-12 rounded-full justify-between"
                onClick={() => setShowPasswordSection(true)}
                data-testid="profile-change-password-btn"
              >
                <span>Change Password</span>
                <ChevronRight size={18} />
              </Button>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="h-12 pr-10"
                      placeholder="Enter current password"
                      data-testid="current-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1 h-10 w-10"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      {showCurrentPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="h-12 pr-10"
                      placeholder="Enter new password"
                      data-testid="new-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1 h-10 w-10"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="h-12"
                    placeholder="Confirm new password"
                    data-testid="confirm-password-input"
                  />
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button
                    onClick={handleChangePassword}
                    disabled={changingPassword}
                    className="flex-1 h-12 rounded-full"
                    data-testid="update-password-btn"
                  >
                    {changingPassword ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      "Update Password"
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowPasswordSection(false);
                      setCurrentPassword("");
                      setNewPassword("");
                      setConfirmPassword("");
                    }}
                    className="h-12 rounded-full"
                    disabled={isForcePasswordChange}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Settings */}
        <Card className="animate-fadeIn" style={{ animationDelay: "0.15s" }}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Settings size={18} />
              Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Reminders - Coming Soon */}
            <div className="flex items-center justify-between py-2 opacity-60">
              <div className="flex items-center gap-3">
                <Bell size={18} className="text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Injection Reminders</p>
                  <p className="text-xs text-muted-foreground">Coming Soon</p>
                </div>
              </div>
              <span className="text-xs bg-muted px-2 py-1 rounded-full text-muted-foreground">
                Soon
              </span>
            </div>

            {/* Theme Selector */}
            <div className="pt-2 border-t border-border/50">
              <div className="flex items-center gap-3 mb-3">
                <Palette size={18} className="text-primary" />
                <p className="text-sm font-medium">Color Theme</p>
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                {themes.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTheme(t.id)}
                    className={`relative flex flex-col items-center p-3 rounded-xl border-2 transition-all ${
                      theme === t.id 
                        ? "border-primary bg-primary/10" 
                        : "border-border hover:border-primary/40 bg-card"
                    }`}
                    data-testid={`theme-${t.id}`}
                  >
                    <div 
                      className="w-8 h-8 rounded-full mb-1.5 shadow-sm border border-white/50"
                      style={{ backgroundColor: t.color }}
                    />
                    <span className="text-xs font-medium text-center leading-tight">
                      {t.name}
                    </span>
                    
                    {theme === t.id && (
                      <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                        <Check size={10} className="text-primary-foreground" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Help & Guide */}
        <Card className="animate-fadeIn" style={{ animationDelay: "0.2s" }}>
          <CardContent className="p-0">
            <Link to="/help" className="block">
              <div className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors rounded-lg">
                <div className="flex items-center gap-3">
                  <HelpCircle size={18} className="text-primary" />
                  <div>
                    <p className="text-sm font-medium">Help & Guide</p>
                    <p className="text-xs text-muted-foreground">Learn how to use LabStack</p>
                  </div>
                </div>
                <ChevronRight size={18} className="text-muted-foreground" />
              </div>
            </Link>
          </CardContent>
        </Card>

        {/* Status Card */}
        {profile?.account_status && profile.account_status !== "active" && (
          <Card className="animate-fadeIn border-destructive/50 bg-destructive/5" style={{ animationDelay: "0.25s" }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertTriangle size={20} className="text-destructive" />
                <div>
                  <p className="font-medium text-destructive">Account Status: {profile.account_status}</p>
                  <p className="text-sm text-muted-foreground">
                    Contact the administrator for assistance.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Danger Zone */}
        <Card className="animate-fadeIn" style={{ animationDelay: "0.3s" }}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-destructive">Danger Zone</CardTitle>
            <CardDescription>
              Irreversible actions that affect your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="outline"
              className="w-full h-12 rounded-full border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
              onClick={() => setShowDeactivateDialog(true)}
              data-testid="profile-deactivate-btn"
            >
              <AlertTriangle size={16} className="mr-2" />
              Deactivate Account
            </Button>
          </CardContent>
        </Card>
      </main>

      {/* Deactivate Confirmation Dialog */}
      <AlertDialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Account?</AlertDialogTitle>
            <AlertDialogDescription>
              This will suspend your account and log you out. You will not be able to log in again until an administrator reactivates your account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeactivate}
              disabled={deactivating}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deactivating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deactivating...
                </>
              ) : (
                "Yes, Deactivate"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
